/**
 * 3PH Cloud ODM helpers — validation, CRF spec checklist, form progress.
 * Rules encoded from the internal Study Build Process spec (not injected at runtime).
 */

import { parseThreePhDataType } from './field-types'

const ODM_NS = 'http://www.cdisc.org/ns/odm/v1.3'
const SDM_NS = 'http://www.cdisc.org/ns/studydesign/v1.0'

const FRAGMENT_VALIDATE_MSG =
  'validate_3ph_odm requires the complete merged ODM document from the XML viewer tab ' +
  '(full <?xml ...><ODM>...</ODM>), not a bare is_fragment=true snippet. ' +
  'Call update_odm_xml first, then pass the merged study XML from the viewer.'

export type ValidationLevel = 'error' | 'warning'

export interface ValidationIssue {
  level: ValidationLevel
  code: string
  message: string
  element?: string
}

export interface ValidationResult {
  valid: boolean
  errors: ValidationIssue[]
  warnings: ValidationIssue[]
  summary: string
  build_complete_ready: boolean
}

export interface CrfFormEntry {
  id: string
  name: string
}

export interface FormBuildProgress {
  entry: CrfFormEntry
  status: 'pending' | 'in_xml' | 'complete'
  matchedFormOid?: string
  itemCount?: number
}

const FORM_LINE_PATTERNS: RegExp[] = [
  /^\s*\d+[\._\)]\s*(.+)$/gm,
  /(?:^|\n)\s*(?:Form|FORM)\s*(?:Name|Title)?\s*[:#\-]\s*(.+?)(?:\n|$)/gi,
  /(?:^|\n)\s*([A-Z][A-Za-z0-9][A-Za-z0-9 \-\/]{6,70})\s*(?:CRF|Case Report Form)/gi,
]

const CRF_FILENAME_RE = /(?:^|\/)(\d+[_\.\)]\s*)?(.+?(?:CRF|Form)[^.]*)/i

function normalizeFormName(name: string): string {
  return name
    .replace(/\s+/g, ' ')
    .replace(/[_]+/g, ' ')
    .replace(/\bCRF\b/gi, '')
    .replace(/\bDRAFT\b/gi, '')
    .trim()
    .toLowerCase()
}

function uniqueEntries(entries: CrfFormEntry[]): CrfFormEntry[] {
  const seen = new Set<string>()
  const out: CrfFormEntry[] = []
  for (const e of entries) {
    const key = normalizeFormName(e.name)
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push(e)
  }
  return out
}

/** Extract form checklist candidates from CRF design spec text (docx extraction). */
export function parseCrfSpecChecklist(text: string, _sourceLabel = 'CRF spec'): CrfFormEntry[] {
  if (!text?.trim()) return []

  const found: CrfFormEntry[] = []

  for (const re of FORM_LINE_PATTERNS) {
    re.lastIndex = 0
    let m: RegExpExecArray | null
    while ((m = re.exec(text)) !== null) {
      const raw = (m[1] || '').trim()
      if (raw.length < 4 || raw.length > 120) continue
      if (/^(table of contents|version|page \d)/i.test(raw)) continue
      found.push({
        id: `crf-${normalizeFormName(raw).replace(/\W+/g, '-')}`,
        name: raw.replace(/\s+/g, ' ').trim(),
      })
    }
  }

  return uniqueEntries(found).map((e, i) => ({
    ...e,
    id: e.id || `crf-${i + 1}`,
  }))
}

export function parseCrfSpecFromFilename(filename: string): CrfFormEntry | null {
  const m = filename.match(CRF_FILENAME_RE)
  if (!m) return null
  const name = (m[2] || '').replace(/[_]+/g, ' ').trim()
  if (name.length < 4) return null
  return {
    id: `file-${normalizeFormName(name).replace(/\W+/g, '-')}`,
    name,
  }
}

export function listFormDefsFromOdm(xml: string): { oid: string; name: string; itemCount: number }[] {
  try {
    const doc = new DOMParser().parseFromString(xml, 'text/xml')
    if (doc.querySelector('parsererror')) return []
    return Array.from(doc.querySelectorAll('FormDef')).map(fd => {
      const oid = fd.getAttribute('OID') || ''
      const name = fd.getAttribute('Name') || oid
      const itemCount = fd.querySelectorAll('ItemGroupRef').length > 0
        ? Array.from(fd.querySelectorAll('ItemGroupRef')).reduce((sum, ref) => {
            const igOid = ref.getAttribute('ItemGroupOID')
            const ig = igOid ? doc.querySelector(`ItemGroupDef[OID="${igOid}"]`) : null
            return sum + (ig?.querySelectorAll('ItemRef').length ?? 0)
          }, 0)
        : 0
      return { oid, name, itemCount }
    })
  } catch {
    return []
  }
}

/** Match CRF checklist entries to FormDefs in current ODM XML.
 *
 * Tries an exact OID match first: entry.id IS the real form_oid whenever the
 * checklist came from parsePresentedPlanForms (a ratified build plan), the
 * common case once a build has actually run. Free-text-parsed checklists
 * (parseCrfSpecChecklist, before any plan exists) synthesize a `crf-...`
 * slug for id instead, which can never coincidentally match a real OID, so
 * they fall through to the pre-existing normalized-name matching unchanged.
 *
 * Without the OID-first check, this disagreed with lib/odm/form-coverage.ts's
 * deriveFormCoverage (OID-first already) whenever a FormDef's real Name
 * attribute in the generated XML didn't textually match the checklist's
 * form_name closely enough to normalize-match (different punctuation,
 * abbreviation, wording) -- found live 2026-07-18: StudyBanner's "10/23
 * forms" (this function) disagreed with BuildPlanFormTree's "22/23 forms
 * complete" (deriveFormCoverage) for the exact same completed build. */
export function matchFormBuildProgress(
  checklist: CrfFormEntry[],
  xml: string,
): FormBuildProgress[] {
  const forms = listFormDefsFromOdm(xml)
  return checklist.map(entry => {
    const match =
      forms.find(f => f.oid === entry.id) ??
      forms.find(f => {
        const needle = normalizeFormName(entry.name)
        const fn = normalizeFormName(f.name)
        return fn === needle || fn.includes(needle) || needle.includes(fn)
      })
    if (!match) return { entry, status: 'pending' as const }
    const status = match.itemCount > 0 ? 'complete' as const : 'in_xml' as const
    return {
      entry,
      status,
      matchedFormOid: match.oid,
      itemCount: match.itemCount,
    }
  })
}

function fragmentNotValidatableResult(): ValidationResult {
  return {
    valid: false,
    errors: [{ level: 'error', code: 'fragment_not_validatable', message: FRAGMENT_VALIDATE_MSG }],
    warnings: [],
    summary: 'Fragment cannot be validated — use merged ODM XML',
    build_complete_ready: false,
  }
}

function resolveOdmNamespace(odm: Element): string | null {
  return odm.getAttribute('xmlns') || odm.namespaceURI
}

/** Bare is_fragment snippets are not full ODM documents — reject before parsing. */
function isBareOdmFragment(xml: string): boolean {
  let body = xml.trim()
  if (body.startsWith('<?xml')) {
    const end = body.indexOf('?>')
    body = end === -1 ? body : body.slice(end + 2).trim()
  }
  body = body.replace(/^\s*<!--[\s\S]*?-->\s*/, '')
  return !/^<ODM[\s/>]/i.test(body)
}

export function validateThreePhOdm(xml: string): ValidationResult {
  const errors: ValidationIssue[] = []
  const warnings: ValidationIssue[] = []

  if (!xml?.trim()) {
    return {
      valid: false,
      errors: [{ level: 'error', code: 'empty', message: 'XML content is empty' }],
      warnings: [],
      summary: 'Empty XML',
      build_complete_ready: false,
    }
  }

  if (isBareOdmFragment(xml)) {
    return fragmentNotValidatableResult()
  }

  const doc = new DOMParser().parseFromString(xml, 'text/xml')
  const parseErr = doc.querySelector('parsererror')
  if (parseErr) {
    const parseMessage = parseErr.textContent || ''
    if (/junk after document element/i.test(parseMessage)) {
      return fragmentNotValidatableResult()
    }
    return {
      valid: false,
      errors: [{
        level: 'error',
        code: 'parse_error',
        message: parseMessage.slice(0, 200) || 'XML parse error',
      }],
      warnings: [],
      summary: 'XML parse error',
      build_complete_ready: false,
    }
  }

  const odm = doc.documentElement
  if (!odm || odm.localName !== 'ODM') {
    return fragmentNotValidatableResult()
  }

  {
    if (odm.getAttribute('ODMVersion') !== '1.3.1') {
      warnings.push({
        level: 'warning',
        code: 'odm_version',
        message: 'ODMVersion should be "1.3.1" for 3PH Cloud',
        element: 'ODM',
      })
    }
    const fileOid = odm.getAttribute('FileOID') || ''
    if (!fileOid.startsWith('3PH.')) {
      warnings.push({
        level: 'warning',
        code: 'file_oid',
        message: 'FileOID should follow 3PH.[indication].[study].001',
        element: 'ODM',
      })
    }
    if (odm.getAttribute('FileType') !== 'Snapshot') {
      warnings.push({ level: 'warning', code: 'file_type', message: 'FileType should be Snapshot', element: 'ODM' })
    }
    const ns = resolveOdmNamespace(odm)
    if (ns !== ODM_NS) {
      errors.push({
        level: 'error',
        code: 'xmlns',
        message: `ODM namespace must be ${ODM_NS}`,
        element: 'ODM',
      })
    }
  }

  const study = doc.querySelector('Study')
  if (!study) {
    errors.push({ level: 'error', code: 'missing_study', message: 'Missing <Study> element' })
  } else {
    const studyOid = study.getAttribute('OID') || ''
    if (!studyOid.endsWith('.1')) {
      warnings.push({
        level: 'warning',
        code: 'study_oid',
        message: 'Study OID should end with .1 (e.g. STUDYNAME.1)',
        element: 'Study',
      })
    }
    if (!study.querySelector('GlobalVariables')) {
      errors.push({ level: 'error', code: 'global_vars', message: 'Study requires GlobalVariables', element: 'Study' })
    }
  }

  const mdv = doc.querySelector('MetaDataVersion')
  if (!mdv) {
    errors.push({ level: 'error', code: 'missing_mdv', message: 'Missing MetaDataVersion' })
  } else {
    const mdvOid = mdv.getAttribute('OID') || ''
    if (!mdvOid.startsWith('MDV.')) {
      warnings.push({
        level: 'warning',
        code: 'mdv_oid',
        message: 'MetaDataVersion OID should be MDV.[StudyName].v[major.minor]',
        element: 'MetaDataVersion',
      })
    }
  }

  for (const item of doc.querySelectorAll('ItemDef')) {
    const oid = item.getAttribute('OID') || ''
    const name = item.getAttribute('Name') || ''
    if (oid && name && oid !== name) {
      errors.push({
        level: 'error',
        code: 'item_name_oid',
        message: `ItemDef Name must equal OID (3PH Cloud): "${name}" ≠ "${oid}"`,
        element: `ItemDef[@OID="${oid}"]`,
      })
    }
    if (!item.querySelector('Question TranslatedText, Question > TranslatedText')) {
      warnings.push({
        level: 'warning',
        code: 'item_question',
        message: `ItemDef ${oid || name} missing Question/TranslatedText`,
        element: `ItemDef[@OID="${oid}"]`,
      })
    }
    const threePh = parseThreePhDataType(item.getAttribute('ThreePhDataType'))
    const hasCodeList = Boolean(item.querySelector('CodeListRef'))
    if (threePh === 'singleselect' || threePh === 'multiselect' || threePh === 'combo') {
      if (!hasCodeList) {
        errors.push({
          level: 'error',
          code: 'codelist_ref',
          message: `ItemDef ${oid} with ThreePhDataType="${threePh}" requires CodeListRef`,
          element: `ItemDef[@OID="${oid}"]`,
        })
      }
    }
    if (threePh === 'multiselect' && hasCodeList) {
      const clOid = item.querySelector('CodeListRef')?.getAttribute('CodeListOID')
      const cl = clOid ? doc.querySelector(`CodeList[OID="${clOid}"]`) : null
      const items = cl?.querySelectorAll('CodeListItem') ?? []
      let aliasOk = false
      items.forEach(cli => {
        cli.querySelectorAll('Alias').forEach(a => {
          if (a.getAttribute('Context') === oid) aliasOk = true
        })
      })
      if (items.length > 0 && !aliasOk) {
        warnings.push({
          level: 'warning',
          code: 'multiselect_alias',
          message: `Multiselect ${oid}: CodeListItems should include Alias Context="${oid}"`,
          element: `ItemDef[@OID="${oid}"]`,
        })
      }
    }
    if (!item.getAttribute('DataType') && !item.getAttribute('ThreePhDataType')) {
      warnings.push({
        level: 'warning',
        code: 'item_type',
        message: `ItemDef ${oid} missing DataType or ThreePhDataType`,
        element: `ItemDef[@OID="${oid}"]`,
      })
    }
    const unitRefOid = item.querySelector('MeasurementUnitRef')?.getAttribute('MeasurementUnitOID')
    if (unitRefOid) {
      const unitDef =
        doc.querySelector(`BasicDefinitions MeasurementUnit[OID="${unitRefOid}"]`) ??
        doc.querySelector(`MeasurementUnit[OID="${unitRefOid}"]`)
      if (!unitDef) {
        errors.push({
          level: 'error',
          code: 'measurement_unit_missing',
          message: `ItemDef ${oid} references MeasurementUnitOID="${unitRefOid}" but no matching MeasurementUnit exists`,
          element: `ItemDef[@OID="${oid}"]`,
        })
      }
    }
    const expectedUnitOid = oid ? `${oid}U` : ''
    if (
      expectedUnitOid &&
      doc.querySelector(`BasicDefinitions MeasurementUnit[OID="${expectedUnitOid}"], MeasurementUnit[OID="${expectedUnitOid}"]`) &&
      !unitRefOid
    ) {
      warnings.push({
        level: 'warning',
        code: 'measurement_unit_ref_missing',
        message: `MeasurementUnit ${expectedUnitOid} exists but ItemDef ${oid} has no MeasurementUnitRef — add <MeasurementUnitRef MeasurementUnitOID="${expectedUnitOid}"/>`,
        element: `ItemDef[@OID="${oid}"]`,
      })
    }
  }

  for (const fd of doc.querySelectorAll('FormDef')) {
    const oid = fd.getAttribute('OID') || ''
    if (oid && !/^F\.\d+/.test(oid)) {
      warnings.push({
        level: 'warning',
        code: 'form_oid',
        message: `FormDef OID "${oid}" — 3PH convention is F.#`,
        element: `FormDef[@OID="${oid}"]`,
      })
    }
    if (!fd.querySelector('Description TranslatedText, Description > TranslatedText')) {
      warnings.push({
        level: 'warning',
        code: 'form_description',
        message: `FormDef ${oid} missing Description/TranslatedText (EDC display name)`,
        element: `FormDef[@OID="${oid}"]`,
      })
    }
  }

  // ItemRef -> ItemDef / ItemGroupRef -> ItemGroupDef referential integrity. A
  // headless parallel-build worker can emit ItemGroupDef/ItemRef shells that
  // reference OIDs it never actually defines -- ported from the Python
  // validator (threeph_odm_validator.py) after a 2026-07-24 incident where a
  // worker returned FormDef + ItemGroupDefs + ItemRefs with zero ItemDef
  // elements at all, submitting cleanly with no error.
  const itemDefOids = new Set(
    Array.from(doc.querySelectorAll('ItemDef'))
      .map(e => e.getAttribute('OID'))
      .filter((v): v is string => Boolean(v)),
  )
  for (const ref of doc.querySelectorAll('ItemRef')) {
    const itemOid = ref.getAttribute('ItemOID') || ''
    if (itemOid && !itemDefOids.has(itemOid)) {
      errors.push({
        level: 'error',
        code: 'dangling_item_ref',
        message: (
          `ItemRef references ItemOID="${itemOid}" but no ItemDef with that OID exists ` +
          'anywhere in the document — the field is a dangling reference and cannot render. ' +
          'Every ItemRef must have a matching ItemDef.'
        ),
        element: `ItemRef[@ItemOID="${itemOid}"]`,
      })
    }
  }

  const itemGroupDefOids = new Set(
    Array.from(doc.querySelectorAll('ItemGroupDef'))
      .map(e => e.getAttribute('OID'))
      .filter((v): v is string => Boolean(v)),
  )
  for (const ref of doc.querySelectorAll('ItemGroupRef')) {
    const igOid = ref.getAttribute('ItemGroupOID') || ''
    if (igOid && !itemGroupDefOids.has(igOid)) {
      errors.push({
        level: 'error',
        code: 'dangling_itemgroup_ref',
        message: (
          `ItemGroupRef references ItemGroupOID="${igOid}" but no ItemGroupDef with that OID ` +
          'exists anywhere in the document.'
        ),
        element: `ItemGroupRef[@ItemGroupOID="${igOid}"]`,
      })
    }
  }

  // 3PH Cloud deviates from strict CDISC ODM+SDM composite documents: despite
  // the xmlns:sdm declaration being required on the ODM root, 3PH Cloud's .NET
  // XmlSerializer never expects the sdm: prefix on actual elements (WorkflowDef,
  // StudyStructure, StudySummary, StudyIndications, InclusionExclusionCriteria,
  // etc.) — it silently drops any element it doesn't recognize, so a namespace
  // mismatch here means data loss, not a parse error.
  for (const e of Array.from(doc.getElementsByTagName('*'))) {
    if (e.namespaceURI === SDM_NS) {
      errors.push({
        level: 'error',
        code: 'sdm_namespace_used',
        message: (
          `<${e.localName}> uses the sdm: namespace prefix — 3PH Cloud requires every element ` +
          'unprefixed (default ODM namespace), even StudyDesignModel elements like ' +
          'WorkflowDef/StudyStructure. XmlSerializer silently drops sdm:-prefixed elements ' +
          'instead of erroring.'
        ),
      })
    }
  }

  if (doc.getElementsByTagName('WorkFlowDef').length > 0) {
    errors.push({
      level: 'error',
      code: 'workflow_def_casing',
      message: (
        'Element name must be "WorkflowDef" (lowercase f), not "WorkFlowDef" — a common typo ' +
        '(present in the source spec doc itself) that silently drops workflow data since ' +
        'XmlSerializer matches element names exactly.'
      ),
    })
  }

  for (const tag of ['Arms', 'Epochs']) {
    for (const _e of Array.from(doc.getElementsByTagName(tag))) {
      errors.push({
        level: 'error',
        code: 'wrapper_element',
        message: (
          `<${tag}> is not a valid 3PH Cloud element — Arm/Epoch must be direct children of ` +
          'StudyStructure, with no wrapper container.'
        ),
        element: tag,
      })
    }
  }

  for (const iec of Array.from(doc.getElementsByTagName('InclusionExclusionCriteria'))) {
    const directChildren = Array.from(iec.children).map(c => c.tagName)
    if (directChildren.includes('Criterion')) {
      errors.push({
        level: 'error',
        code: 'criterion_wrapper',
        message: (
          'Criterion elements must be nested under InclusionCriteria/ExclusionCriteria wrapper ' +
          'elements, not direct children of InclusionExclusionCriteria.'
        ),
      })
    }
  }

  for (const c of Array.from(doc.getElementsByTagName('Criterion'))) {
    const coid = c.getAttribute('OID') || ''
    if (c.getAttribute('Type') !== null) {
      errors.push({
        level: 'error',
        code: 'criterion_type_attr',
        message: (
          `Criterion OID="${coid}" uses a Type attribute — 3PH Cloud expects Name="..." (a ` +
          'descriptive label) instead. Inclusion/Exclusion classification comes from the ' +
          'InclusionCriteria/ExclusionCriteria wrapper, not a Type attribute.'
        ),
        element: `Criterion[@OID="${coid}"]`,
      })
    }
    if (!c.getAttribute('Name')) {
      errors.push({
        level: 'error',
        code: 'criterion_missing_name',
        message: `Criterion OID="${coid}" is missing the required Name attribute.`,
        element: `Criterion[@OID="${coid}"]`,
      })
    }
  }

  for (const sp of Array.from(doc.getElementsByTagName('StudyParameter'))) {
    if (sp.getAttribute('Value') !== null || sp.getAttribute('Name') !== null) {
      errors.push({
        level: 'error',
        code: 'study_parameter_attrs',
        message: (
          'StudyParameter uses Name/Value attributes — 3PH Cloud requires OID/Term/ShortName ' +
          '(e.g. ShortName="PLANNEDSUB"/"PLANNEDSITE"). Name/Value data is silently dropped and ' +
          "breaks arm/participant quota logic in 3PH Cloud's AddParticipantPopup."
        ),
      })
    }
    const missing = ['OID', 'Term', 'ShortName'].filter(a => !sp.getAttribute(a))
    if (missing.length > 0) {
      errors.push({
        level: 'error',
        code: 'study_parameter_missing_attrs',
        message: `StudyParameter missing required attribute(s): ${missing.join(', ')}.`,
      })
    }
  }

  for (const arm of Array.from(doc.getElementsByTagName('Arm'))) {
    if (!arm.getAttribute('Version')) {
      errors.push({
        level: 'error',
        code: 'arm_version_missing',
        message: `Arm OID="${arm.getAttribute('OID') || ''}" is missing the required Version attribute.`,
      })
    }
  }

  for (const segd of Array.from(doc.getElementsByTagName('StudyEventGroupDef'))) {
    const segdOid = segd.getAttribute('OID') || ''
    if (!segd.getAttribute('ArmOID')) {
      errors.push({
        level: 'error',
        code: 'study_event_group_arm_oid_missing',
        message: `StudyEventGroupDef OID="${segdOid}" is missing the required ArmOID attribute.`,
      })
    }
    if (!segd.getAttribute('EpochOID')) {
      errors.push({
        level: 'error',
        code: 'study_event_group_epoch_oid_missing',
        message: `StudyEventGroupDef OID="${segdOid}" is missing the required EpochOID attribute.`,
      })
    }
  }

  for (const we of Array.from(doc.getElementsByTagName('WorkflowEnd'))) {
    if (!we.getAttribute('EndOID')) {
      errors.push({
        level: 'error',
        code: 'workflow_end_missing_endoid',
        message: (
          'WorkflowEnd is missing the required EndOID attribute (it uses EndOID, not OID) — ' +
          'and the value must be a real StudyEventDef OID already in this document, never a ' +
          'made-up sentinel like "WF.END".'
        ),
      })
    }
  }

  const protocols = Array.from(doc.getElementsByTagName('Protocol'))
  for (const protocol of protocols) {
    if (!Array.from(protocol.children).some(c => c.tagName === 'StudyTiming')) {
      errors.push({
        level: 'error',
        code: 'study_timing_missing',
        message: (
          "Protocol is missing the required StudyTiming element. It's required even when " +
          'there is no redo/blanking logic — emit a bare self-closing element: ' +
          '<StudyTiming OID="ST.1" Name="Study Timing"/>. Omitting it entirely throws a ' +
          'generic null-reference error on upload to 3PH Cloud.'
        ),
      })
    }
  }

  // AB#19877 (v3.0 review feedback): a real generation
  // attempt named StudyEventDef OIDs "DISSE.#" -- "DIS" leaked from an
  // unrelated legacy study (DISRUPT) prefixed onto the spec's "SE.#".
  for (const sed of Array.from(doc.getElementsByTagName('StudyEventDef'))) {
    const oid = sed.getAttribute('OID') || ''
    if (oid && !oid.startsWith('SE.')) {
      errors.push({
        level: 'error',
        code: 'study_event_def_oid_prefix',
        message: (
          `StudyEventDef OID="${oid}" must start with "SE." (e.g. "SE.1", "SE.OTHER") — never ` +
          'a legacy study\'s own acronym prefixed onto it (e.g. "DISSE.#" leaking a past ' +
          "study's naming convention into a new study)."
        ),
        element: `StudyEventDef[@OID="${oid}"]`,
      })
    }
  }

  // AB#19877 comment #4: "StudyEventGroupRef is still not present in the XML
  // ... otherwise, it won't load into legacy" -- required for every
  // StudyEventGroupDef in the document.
  const studyEventGroupDefOids = new Set(
    Array.from(doc.getElementsByTagName('StudyEventGroupDef'))
      .map(e => e.getAttribute('OID'))
      .filter((v): v is string => Boolean(v)),
  )
  if (studyEventGroupDefOids.size > 0) {
    const segrOidsSeen = new Set<string>()
    for (const protocol of protocols) {
      for (const ref of Array.from(protocol.children)) {
        if (ref.tagName === 'StudyEventGroupRef') {
          const v = ref.getAttribute('StudyEventGroupOID')
          if (v) segrOidsSeen.add(v)
        }
      }
    }
    if (segrOidsSeen.size === 0) {
      errors.push({
        level: 'error',
        code: 'study_event_group_ref_missing',
        message: (
          'Protocol is missing the required StudyEventGroupRef element — required for 3PH ' +
          'legacy load compatibility whenever a StudyEventGroupDef exists in the document. Add ' +
          '<StudyEventGroupRef StudyEventGroupOID="[StudyEventGroupDef.OID]" Mandatory="No"/> ' +
          'as a child of Protocol for every StudyEventGroupDef.'
        ),
      })
    }
  }

  for (const ref of Array.from(doc.getElementsByTagName('StudyEventGroupRef'))) {
    const segrOid = ref.getAttribute('StudyEventGroupOID') || ''
    if (segrOid && !studyEventGroupDefOids.has(segrOid)) {
      errors.push({
        level: 'error',
        code: 'dangling_study_event_group_ref',
        message: (
          `StudyEventGroupRef references StudyEventGroupOID="${segrOid}" but no ` +
          'StudyEventGroupDef with that OID exists anywhere in the document.'
        ),
      })
    }
  }

  for (const fd of Array.from(doc.getElementsByTagName('FormDef'))) {
    if (!Array.from(fd.children).some(c => c.tagName === 'ItemGroupRef')) {
      warnings.push({
        level: 'warning',
        code: 'form_itemgroupref_missing',
        message: (
          `FormDef OID="${fd.getAttribute('OID') || ''}" has no ItemGroupRef — this is a ` +
          "normal stub during incremental building, but 3PH Cloud's upload gate has zero " +
          'tolerance for it: every FormDef referenced by a StudyEventDef must have at least ' +
          'one ItemGroupRef before this study is upload-ready.'
        ),
        element: `FormDef[@OID="${fd.getAttribute('OID') || ''}"]`,
      })
    }
  }

  const valid = errors.length === 0
  const hasStubForms = warnings.some(w => w.code === 'form_itemgroupref_missing')
  // build_complete_ready is stricter than valid: a document can be `valid`
  // (zero errors) while still carrying a form_itemgroupref_missing warning
  // -- an empty-stub FormDef with no fields. That warning is deliberately
  // non-blocking during incremental building, but it must never coexist with
  // a "this build is complete" claim (Incident B, 2026-07-08).
  const buildCompleteReady = valid && !hasStubForms
  const summary = valid
    ? `3PH validation passed (${warnings.length} warning${warnings.length === 1 ? '' : 's'})`
    : `${errors.length} error${errors.length === 1 ? '' : 's'}, ${warnings.length} warning${warnings.length === 1 ? '' : 's'}`

  return { valid, errors, warnings, summary, build_complete_ready: buildCompleteReady }
}
